import { useCallback, useEffect, useState } from "react";
import { getOperation } from "../services/api";
import type { IOperationResponse } from "../services/api";
import {
  isOperationNonTerminal,
  useOperationSSE,
} from "./useOperationSSE";

export type WatchedOperationStatus = "loading" | "ready" | "missing";

export interface UseWatchedOperationResult {
  operation: IOperationResponse | null;
  status: WatchedOperationStatus;
  refresh: () => Promise<void>;
}

const POLL_INTERVAL_MS = 2000;

/**
 * Loads and live-tracks a single operation for the receipt screen.
 * Uses create-response seed (optional), SSE, and poll until terminal.
 */
export function useWatchedOperation(
  linkId: string | null | undefined,
  operationId: string | null | undefined,
  seed?: IOperationResponse | null
): UseWatchedOperationResult {
  const seedMatches =
    !!seed && !!operationId && seed.operationId === operationId;

  const [operation, setOperation] = useState<IOperationResponse | null>(() =>
    seedMatches ? seed! : null
  );
  const [initialLoadDone, setInitialLoadDone] = useState(seedMatches);

  const upsertOperation = useCallback(
    (op: IOperationResponse) => {
      if (op.operationId === operationId) {
        setOperation(op);
      }
    },
    [operationId]
  );

  useOperationSSE(
    linkId,
    operation ? [operation] : [],
    upsertOperation,
    undefined,
    { watchOperationId: operationId ?? undefined }
  );

  // Initial fetch (and re-fetch when ids change). Seed paints first; GET reconciles.
  useEffect(() => {
    if (!linkId || !operationId) {
      setOperation(null);
      setInitialLoadDone(false);
      return;
    }

    let cancelled = false;
    if (!seedMatches) {
      setInitialLoadDone(false);
      setOperation(null);
    }

    getOperation(linkId, operationId)
      .then((op) => {
        if (!cancelled) setOperation(op);
      })
      .catch((error) => {
        console.error("[useWatchedOperation] Failed to fetch operation:", error);
      })
      .finally(() => {
        if (!cancelled) setInitialLoadDone(true);
      });

    return () => {
      cancelled = true;
    };
  }, [linkId, operationId, seedMatches]);

  // Poll while non-terminal so receipt advances even if SSE is blocked (ngrok).
  useEffect(() => {
    if (!linkId || !operationId || !operation) return;
    if (!isOperationNonTerminal(operation)) return;

    const id = window.setInterval(() => {
      void getOperation(linkId, operationId, { raasSilentOffline: true })
        .then((op) => setOperation(op))
        .catch((error) =>
          console.error("[useWatchedOperation] poll failed:", error)
        );
    }, POLL_INTERVAL_MS);

    return () => window.clearInterval(id);
  }, [linkId, operationId, operation?.status, operation?.operationId]);

  let status: WatchedOperationStatus;
  if (operation) {
    status = "ready";
  } else if (!initialLoadDone) {
    status = "loading";
  } else {
    status = "missing";
  }

  const refresh = useCallback(async () => {
    if (!linkId || !operationId) return;
    const op = await getOperation(linkId, operationId);
    setOperation(op);
  }, [linkId, operationId]);

  return { operation, status, refresh };
}
